import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMessageInputContext, useChannelStateContext, useChatContext } from 'stream-chat-react'
import ReactDOM from 'react-dom'

import './MessagingInput.css'
// import { isMobileOnly } from 'mobile-device-detect'
import { LightningBoltSmall } from '../../assets'
import type { StreamChatGenerics } from '../../types'
import { isGroupChannel } from '../../utils/channelHelpers'
import { useGiphyContext } from '../../Giphy'
import { AppPaperClipIcon } from 'components/Common/AppSvgIcons/AppPaperClipIcon'
import { AppEmojiIcon } from 'components/Common/AppSvgIcons/AppEmojiIcon'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'
import { AppPlayIcon } from 'components/Common/AppSvgIcons/AppPlayIcon'
import MentionTextArea from 'components/AppKanbanBoard/AppTask/AppComment/MentionTextArea.component'
import { useGetTeammember } from 'hooks/useGetTeammember'
import { AppEmojiPicker } from 'components/AppShared/AppEmojiPicker/AppEmojiPicker.component'
import { getFileIconFromMimeType } from 'utils/file-utils'
import { VoiceRecorder } from '../VoiceRecorder'
import { ReactComponent as MicIcon } from 'assets/svgs/microphone-icon.svg'
import { AppTask } from 'components/AppKanbanBoard/AppTask/AppTask.component'
import { AppTaskCreationConfirmation } from 'components/AppKanbanBoard/AppTaskCreationConfirmation/AppTaskCreationConfirmation.component'
import { UserTask, UserTaskSource } from '__generated__/api-types-and-hooks'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import AppCrossIcon from 'components/Common/AppSvgIcons/AppModalCrossIcon'
import { getTenantId } from 'utils/getTenantId'
import { useGetBusinessProfile } from 'appHooks/useGetBusinessProfile'

const Portal = ({ children }) => {
  if (typeof window === 'undefined') return null
  return ReactDOM.createPortal(children, document.body)
}

// Note: Chat context generation functions removed - play descriptions are now empty by default

const GiphyIcon = () => (
  <div className="giphy-icon__wrapper">
    <LightningBoltSmall />
    <p className="giphy-icon__text">GIPHY</p>
  </div>
)

interface MessagingInputProps {
  showChannelCreate?: boolean
}

const MessagingInput = ({ showChannelCreate = false }: MessagingInputProps) => {
  const { t } = useTranslation()
  const { giphyState, setGiphyState } = useGiphyContext()
  const messageInput = useMessageInputContext<StreamChatGenerics>()
  const teamMembers = useGetTeammember()

  // Get assignee options and advisor data for play creation
  const { assigneeOptions, advisorsData } = useGetBusinessProfile({ enabled: true })

  // Debug logging
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [quotedMessage, setQuotedMessage] = useState<any>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [transcript, setTranscript] = useState<string>('')
  const [voiceDurationSeconds, setVoiceDurationSeconds] = useState<number | null>(null)

  // Play creation modal state
  const [showCreatePlayModal, setShowCreatePlayModal] = useState(false)
  const [showPlaySuccessModal, setShowPlaySuccessModal] = useState(false)
  const [showPlayErrorModal, setShowPlayErrorModal] = useState(false)
  const [createdUserTask, setCreatedUserTask] = useState<UserTask | undefined>(undefined)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const emojiButtonRef = useRef<HTMLButtonElement>(null)
  const [emojiPickerPosition, setEmojiPickerPosition] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  })

  const handleCreatedUserTask = useCallback((task: React.SetStateAction<UserTask>) => {
    if (typeof task === 'function') {
      setCreatedUserTask((prev) => task(prev!))
    } else {
      setCreatedUserTask(task)
    }
  }, [])

  // Get channel and client context for determining chat type
  const { channel } = useChannelStateContext<StreamChatGenerics>()
  const { client } = useChatContext<StreamChatGenerics>()

  // Determine placeholder text based on chat type
  const otherMembers = Object.values(channel?.state?.members || {}).filter(
    (member) => member.user?.id !== client?.user?.id
  )

  // Determine if this is a 1-1 chat (exactly one other member) or group chat
  const isOneOnOneChat = !isGroupChannel(channel)

  // Extract channel members for mentions - use channel members for group chats, all team members for 1-1 chats
  const mentionableMembers = useMemo(() => {
    // For 1-1 chats: show all team members + advisors
    if (isOneOnOneChat) {
      return teamMembers
    }

    // For group chats: only show members in the group
    if (!channel || !client?.user?.id) return []

    // Ensure channel state is available
    const membersObj = channel.state?.members || {}
    const members = Object.values(membersObj)
      .filter((member) => member.user?.id !== client.user?.id)
      .filter((member) => member.user?.id && member.user?.firstName && member.user?.lastName)
      .map((member) => ({
        id: member.user?.id || '',
        firstName: (member.user?.firstName as string) || '',
        lastName: (member.user?.lastName as string) || '',
      }))

    return members
  }, [isOneOnOneChat, teamMembers, channel, client.user?.id])

  const getPlaceholderText = () => {
    // If we're creating a new channel, show generic placeholder
    if (showChannelCreate) {
      return t('chat.input.placeholderCreateChannel')
    }

    if (!channel || !client?.user?.id) {
      return t('chat.typeMessage')
    }

    // Get other members (excluding current user)

    // If it's a 1-1 chat (exactly one other member)
    if (otherMembers.length === 1) {
      const otherUser = otherMembers[0].user
      const firstName = otherUser?.firstName || otherUser?.name?.split(' ')[0] || ''
      const lastName = otherUser?.lastName || otherUser?.name?.split(' ')[1] || ''

      if (firstName && lastName) {
        return t('chat.input.placeholderUserFull', { firstName, lastName })
      } else if (firstName) {
        return t('chat.input.placeholderUser', { name: firstName })
      } else if (otherUser?.name) {
        return t('chat.input.placeholderUser', { name: otherUser.name })
      }
      return t('chat.typeMessage')
    }

    // For group chats (more than one other member)
    return t('chat.input.placeholderGroup')
  }

  // Create a custom keydown handler for the textarea
  useEffect(() => {
    const textareaElement = messageInput.textareaRef?.current
    if (!textareaElement) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
        e.preventDefault()
        messageInput.handleSubmit(e as any)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        messageInput.setText('')
        setQuotedMessage(null) // Clear quoted message on escape
      }
    }

    textareaElement.addEventListener('keydown', handleKeyDown)
    return () => {
      textareaElement.removeEventListener('keydown', handleKeyDown)
    }
  }, [messageInput])

  // Listen for reply events
  useEffect(() => {
    const handleReplyEvent = (event: CustomEvent) => {
      const { message, quotedText } = event.detail
      setQuotedMessage(message)

      // Set the quoted text in the input
      const currentText = messageInput.text || ''
      const newText = quotedText + currentText
      messageInput.setText(newText)
    }

    window.addEventListener('chat-reply', handleReplyEvent as EventListener)
    return () => window.removeEventListener('chat-reply', handleReplyEvent as EventListener)
  }, [messageInput])

  const handleMentionChange = useCallback(
    (newValue: string, newPlainTextValue: string, mentions: any[]) => {
      const deletePressed = newValue.length < messageInput.text.length

      if (messageInput.text.length === 1 && deletePressed) {
        setGiphyState(false)
      }

      if (!giphyState && newValue.startsWith('/giphy') && !messageInput.numberOfUploads) {
        const cleanedValue = newValue.replace('/giphy', '')
        messageInput.setText(cleanedValue)
        setGiphyState(true)
      } else {
        messageInput.setText(newValue)
      }
    },
    [giphyState, messageInput, setGiphyState]
  )

  const onSendMessage = async (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()

    // If there's a quoted message, clear it
    if (quotedMessage) {
      setQuotedMessage(null)
    }

    // If we have a transcript, send message with custom data directly
    if ((transcript || voiceDurationSeconds !== null) && channel) {
      try {
        // Convert file uploads to attachments format
        const attachments: any[] = []

        // Add file uploads
        Object.values(messageInput.fileUploads).forEach((upload) => {
          if (upload.state === 'finished' && upload.file) {
            attachments.push({
              type: 'file',
              file: upload.file,
              file_name: upload.file.name,
              mime_type: upload.file.type,
              file_size: upload.file.size,
              asset_url: upload.url,
            })
          }
        })

        // Add image uploads
        Object.values(messageInput.imageUploads).forEach((upload) => {
          if (upload.state === 'finished' && upload.file) {
            attachments.push({
              type: 'image',
              file: upload.file,
              file_name: upload.file.name,
              mime_type: upload.file.type,
              file_size: upload.file.size,
              image_url: upload.url,
            })
          }
        })

        const messageData = {
          text: messageInput.text || '',
          attachments: attachments,
          custom: {
            transcript: transcript,
            voiceDurationSeconds: voiceDurationSeconds,
          },
        }

        await channel.sendMessage(messageData)

        // Clear the input and transcript after successful send
        messageInput.setText('')
        setTranscript('')
        setVoiceDurationSeconds(null)

        // Clear all uploaded files
        Object.keys(messageInput.fileUploads).forEach((uploadId) => {
          if (messageInput.removeFile) {
            messageInput.removeFile(uploadId)
          }
        })

        Object.keys(messageInput.imageUploads).forEach((uploadId) => {
          if (messageInput.removeImage) {
            messageInput.removeImage(uploadId)
          }
        })

        // Clear any other editing state
        if (messageInput.clearEditingState) {
          messageInput.clearEditingState()
        }

        return // Exit early since we handled the send
      } catch (error) {
        console.error('Error sending message with transcript:', error)
        // Fall back to normal submit
      }
    }

    // Normal submit for messages without transcript
    messageInput.handleSubmit(event)

    // Clear transcript after submission
    if (transcript) setTranscript('')
    if (voiceDurationSeconds !== null) setVoiceDurationSeconds(null)
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      // If there's a quoted message, we need to handle it specially
      if (quotedMessage) {
        // Clear the quoted message state
        setQuotedMessage(null)
      }

      // If we have a transcript, send message with custom data directly
      if ((transcript || voiceDurationSeconds !== null) && channel) {
        try {
          // Convert file uploads to attachments format
          const attachments: any[] = []

          // Add file uploads
          Object.values(messageInput.fileUploads).forEach((upload) => {
            if (upload.state === 'finished' && upload.file) {
              attachments.push({
                type: 'file',
                file: upload.file,
                file_name: upload.file.name,
                mime_type: upload.file.type,
                file_size: upload.file.size,
                asset_url: upload.url,
              })
            }
          })

          // Add image uploads
          Object.values(messageInput.imageUploads).forEach((upload) => {
            if (upload.state === 'finished' && upload.file) {
              attachments.push({
                type: 'image',
                file: upload.file,
                file_name: upload.file.name,
                mime_type: upload.file.type,
                file_size: upload.file.size,
                image_url: upload.url,
              })
            }
          })

          const messageData = {
            text: messageInput.text || '',
            attachments: attachments,
            custom: {
              transcript: transcript,
              voiceDurationSeconds: voiceDurationSeconds,
            },
          }

          await channel.sendMessage(messageData)

          // Clear the input and transcript after successful send
          messageInput.setText('')
          setTranscript('')
          setVoiceDurationSeconds(null)

          // Clear all uploaded files
          Object.keys(messageInput.fileUploads).forEach((uploadId) => {
            if (messageInput.removeFile) {
              messageInput.removeFile(uploadId)
            }
          })

          Object.keys(messageInput.imageUploads).forEach((uploadId) => {
            if (messageInput.removeImage) {
              messageInput.removeImage(uploadId)
            }
          })

          // Clear any other editing state
          if (messageInput.clearEditingState) {
            messageInput.clearEditingState()
          }

          return // Exit early since we handled the send
        } catch (error) {
          console.error('Error sending message with transcript:', error)
          // Fall back to normal submit
        }
      }

      // Normal submit for messages without transcript
      messageInput.handleSubmit(e)

      // Clear transcript after submission
      if (transcript) setTranscript('')
      if (voiceDurationSeconds !== null) setVoiceDurationSeconds(null)
    },
    [messageInput, quotedMessage, transcript, voiceDurationSeconds, channel]
  )

  // Handle emoji picker clicks outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false)
      }
    }

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showEmojiPicker])

  // Update emoji picker position when opened
  useEffect(() => {
    if (showEmojiPicker && emojiButtonRef.current) {
      const rect = emojiButtonRef.current.getBoundingClientRect()
      setEmojiPickerPosition({
        left: rect.left,
        top: rect.bottom + window.scrollY + 8, // 8px margin below button
      })
    }
  }, [showEmojiPicker])

  const uploadFiles = useCallback(
    (files: File[]) => {
      messageInput.uploadNewFiles(files)
    },
    [messageInput]
  )

  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files

    if (files && files.length > 0) {
      const fileArray = Array.from(files)

      // Use safe uploader that adapts to available APIs
      uploadFiles(fileArray)
    }
    // Reset the input
    event.target.value = ''
  }

  // Handle emoji selection
  const handleEmojiClick = (emojiObject: any) => {
    const emoji = emojiObject.emoji
    const currentText = messageInput.text || ''
    const newText = currentText + emoji
    messageInput.setText(newText)
    setShowEmojiPicker(false)
  }

  // Handle voice message
  const handleVoiceMessage = useCallback(
    async (audioBlob: Blob, transcript?: string, durationSeconds?: number) => {
      try {
        // Create a File object from the Blob
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, {
          type: 'audio/webm;codecs=opus',
        })

        // Upload the voice file using the existing file upload mechanism
        uploadFiles([audioFile])

        // Store transcript in state instead of adding to text input
        if (transcript) setTranscript(transcript)
        if (typeof durationSeconds === 'number') setVoiceDurationSeconds(durationSeconds)

        // Voice message processed successfully - hide recorder
        setIsRecording(false)
      } catch (error) {
        console.error('Error processing voice message:', error)
        setIsRecording(false)
      }
    },
    [uploadFiles]
  )

  // Handle voice recorder cancel
  const handleVoiceCancel = useCallback(() => {
    setIsRecording(false)
  }, [])

  // Start recording
  const startVoiceRecording = useCallback(() => {
    setIsRecording(true)
  }, [])

  // Helper function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <>
      <div className="bg-secondary xs:fixed xs:left-0 xs:bottom-0 xs:w-full sm:static">
        {/* Mobile version - show below md:868px */}
        {/* <div className="!bg-secondary md:hidden">
          <div className="grow">
            <div>
              {giphyState && !messageInput.numberOfUploads && <GiphyIcon />}

              <MentionTextArea
                value={messageInput.text}
                placeholder="Write a message..."
                onChange={handleMentionChange}
                teamMembers={teamMembers}
                textAreaRef={messageInput.textareaRef as React.RefObject<HTMLTextAreaElement>}
                onSubmit={() => {
                  const syntheticEvent = new Event('submit')
                  messageInput.handleSubmit(syntheticEvent as any)
                }}
              />
            </div>
          </div>
          <div className="flex items-center justify-between pb-3">
            <div className="flex items-center justify-center space-x-2">
              <div className="mt-4 ml-4 cursor-pointer" onClick={handleFileUpload}>
                <AppPaperClipIcon color="#7C7C7C" />
              </div>
              <div
                className="emoji-button mr-2 mt-3"
                role="button"
                aria-roledescription="button"
                onClick={
                  messageInput.emojiPickerIsOpen
                    ? messageInput.closeEmojiPicker
                    : messageInput.openEmojiPicker
                }
                ref={messageInput.emojiPickerRef}
              >
                <AppEmojiIcon />
              </div>
              <div className="absolute left-0 right-0">
                <AppEmojiPicker
                  onEmojiClick={handleEmojiClick}
                  isOpen={showEmojiPicker}
                  onToggle={() => setShowEmojiPicker(false)}
                  showEmojiIcon={false}
                />
              </div>
            </div>
            <div
              className="mr-3 mt-3"
              role="button"
              aria-roledescription="button"
              onClick={onSendMessage}
            >
              <AppSendMessageIcon />
            </div>
          </div>
        </div> */}
      </div>
      <div className="px-4 py-3 bg-white border-app-grey-25">
        <div className="relative">
          {giphyState && !messageInput.numberOfUploads && <GiphyIcon />}

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            multiple
            accept="*/*"
          />

          {/* Emoji picker */}
          {showEmojiPicker && (
            <Portal>
              <div
                ref={emojiPickerRef}
                className="fixed z-50"
                style={{
                  left: emojiPickerPosition.left,
                  top: emojiPickerPosition.top,
                }}
              >
                <AppEmojiPicker
                  onEmojiClick={handleEmojiClick}
                  isOpen={showEmojiPicker}
                  onToggle={() => setShowEmojiPicker(false)}
                  showEmojiIcon={false}
                />
              </div>
            </Portal>
          )}

          {/* Quoted Message Preview */}
          {quotedMessage && (
            <div className="mb-3 p-3 bg-app-grey-10 border-l-4 border-app-primary-60 rounded-r-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-xs text-app-grey-70 mb-1">
                    {t('chat.input.replyingTo', {
                      name: quotedMessage.user?.firstName || t('chat.input.user'),
                    })}
                  </div>
                  <div className="text-sm text-app-grey-90 line-clamp-2">
                    {quotedMessage.text || t('chat.input.messageFallback')}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setQuotedMessage(null)
                    // Clear the quoted text from the input
                    const currentText = messageInput.text || ''
                    const quotedTextPattern = /^> .+:\s.+\n\n/
                    const newText = currentText.replace(quotedTextPattern, '')
                    messageInput.setText(newText)
                  }}
                  className="ml-2 p-1 text-app-grey-60 hover:text-app-grey-90 transition-colors"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Voice recorder - show above input when active */}
          {isRecording && (
            <VoiceRecorder
              onVoiceMessage={handleVoiceMessage}
              onCancel={handleVoiceCancel}
              className="mb-3"
              autoStart={true}
            />
          )}

          <form onSubmit={handleSubmit}>
            <div className="bg-app-grey-5 rounded-3xl px-4 py-3 border border-app-grey-25">
              {/* Input field - Full width on top */}
              <div className="w-full mb-3">
                <div className="min-h-[20px]">
                  <MentionTextArea
                    value={messageInput.text}
                    placeholder={getPlaceholderText()}
                    onChange={handleMentionChange}
                    teamMembers={mentionableMembers}
                    textAreaRef={messageInput.textareaRef as React.RefObject<HTMLTextAreaElement>}
                    onSubmit={() => {
                      const syntheticEvent = new Event('submit')
                      messageInput.handleSubmit(syntheticEvent as any)
                    }}
                  />
                </div>
              </div>

              {/* Icons row - Below the input */}
              <div className="flex items-center justify-between">
                {/* Left side icons */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="p-1 rounded-full hover:bg-app-grey-20 transition-colors"
                    onClick={handleFileUpload}
                  >
                    <AppPaperClipIcon color="#7C7C7C" />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded-full hover:bg-app-grey-20 transition-colors"
                    onClick={() => setShowCreatePlayModal(true)}
                    title={t('chat.input.createPlay')}
                  >
                    <AppPlayIcon className="w-5 h-5 text-app-grey-60" />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded-full hover:bg-app-grey-20 transition-colors"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    ref={emojiButtonRef}
                  >
                    <AppEmojiIcon />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded-full hover:bg-app-grey-20 transition-colors"
                    onClick={startVoiceRecording}
                    title={t('chat.recordVoiceMessage')}
                  >
                    <MicIcon className="w-5 h-5 text-app-grey-60" />
                  </button>
                </div>

                {/* Right side send button */}
                <button
                  type="submit"
                  className={`p-2 rounded-full transition-all ${
                    messageInput.text.trim() ||
                    messageInput.numberOfUploads > 0 ||
                    voiceDurationSeconds !== null
                      ? 'bg-app-primary-60 hover:bg-app-primary-70 text-white'
                      : 'bg-app-grey-30 text-app-grey-55 cursor-not-allowed'
                  }`}
                  onClick={onSendMessage}
                  disabled={
                    !messageInput.text.trim() &&
                    messageInput.numberOfUploads === 0 &&
                    voiceDurationSeconds === null
                  }
                >
                  <AppSendMessageIcon />
                </button>
              </div>
            </div>
          </form>

          {/* File Preview Section */}
          {(Object.keys(messageInput.fileUploads).length > 0 ||
            Object.keys(messageInput.imageUploads).length > 0) && (
            <div className="mt-3 space-y-2">
              {/* Image Uploads */}
              {Object.values(messageInput.imageUploads).map((upload, index) => (
                <div
                  key={`image-${upload.id || index}`}
                  className="flex items-center gap-3 p-3 bg-app-grey-5 rounded-lg border border-app-grey-25"
                >
                  {/* Image Preview */}
                  <div className="flex-shrink-0 relative">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-app-grey-20 border border-app-grey-30">
                      {upload.url ? (
                        <>
                          <img
                            src={upload.url}
                            alt={upload.file?.name || 'Image preview'}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to placeholder if image fails to load
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              const fallback = target.nextElementSibling as HTMLElement
                              if (fallback) fallback.style.display = 'flex'
                            }}
                          />
                          <div className="w-full h-full hidden items-center justify-center text-app-grey-60">
                            🖼️
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-app-grey-60">
                          🖼️
                        </div>
                      )}
                    </div>

                    {/* Upload Status Overlay */}
                    {upload.state === 'uploading' && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    {upload.state === 'finished' && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    )}
                    {upload.state === 'failed' && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-app-grey-80 truncate">
                          {upload.file?.name || 'Image'}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-app-grey-60">
                          {upload.file?.size && <span>{formatFileSize(upload.file.size)}</span>}
                          {upload.state === 'uploading' && (
                            <span className="text-app-primary-60">Uploading...</span>
                          )}
                          {upload.state === 'finished' && (
                            <span className="text-green-600">Uploaded</span>
                          )}
                          {upload.state === 'failed' && (
                            <span className="text-red-600">Failed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {upload.state !== 'uploading' && (
                    <button
                      type="button"
                      onClick={() =>
                        messageInput.removeImage &&
                        messageInput.removeImage(upload.id || index.toString())
                      }
                      className="flex-shrink-0 p-1 text-app-grey-60 hover:text-red-500 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}

              {/* File Uploads */}
              {Object.values(messageInput.fileUploads).map((upload, index) => (
                <div
                  key={`file-${upload.id || index}`}
                  className="flex items-center gap-3 p-3 bg-app-grey-5 rounded-lg border border-app-grey-25"
                >
                  <div className="flex-shrink-0">
                    {upload.state === 'uploading' ? (
                      <div className="w-8 h-8 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-app-primary-60 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : upload.state === 'finished' ? (
                      <div className="w-8 h-8 flex items-center justify-center text-green-500">
                        ✓
                      </div>
                    ) : (
                      <div className="w-8 h-8 flex items-center justify-center text-red-500">✗</div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {getFileIconFromMimeType(upload.file?.type || '')}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-app-grey-80 truncate">
                          {upload.file?.name || 'File'}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-app-grey-60">
                          {upload.file?.size && <span>{formatFileSize(upload.file.size)}</span>}
                          {upload.state === 'uploading' && (
                            <span className="text-app-primary-60">Uploading...</span>
                          )}
                          {upload.state === 'finished' && (
                            <span className="text-green-600">Uploaded</span>
                          )}
                          {upload.state === 'failed' && (
                            <span className="text-red-600">Failed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {upload.state !== 'uploading' && (
                    <button
                      type="button"
                      onClick={() =>
                        messageInput.removeFile &&
                        messageInput.removeFile(upload.id || index.toString())
                      }
                      className="flex-shrink-0 p-1 text-app-grey-60 hover:text-red-500 transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Play Modal */}
      {showCreatePlayModal && (
        <Portal>
          <AppModalBox
            Icon={AppCrossIcon}
            height="max-h-[95vh] min-h-[50vh] h-fit"
            width="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] xl:max-w-2xl mx-2 sm:mx-4 md:mx-auto"
            titleSize="sm"
            title={t('chat.input.createNewPlay')}
            showBorder={false}
            onClose={() => setShowCreatePlayModal(false)}
            onBack={() => setShowCreatePlayModal(false)}
          >
            <AppTask
              tenantId={getTenantId()}
              userId={client?.user?.id || ''}
              source={UserTaskSource.General}
              setShowTaskModal={setShowCreatePlayModal}
              setShowTaskSuccessModal={setShowPlaySuccessModal}
              setShowTaskErrorModal={setShowPlayErrorModal}
              setCreatedUserTask={handleCreatedUserTask}
              refetch={() => {}} // Play board will auto-refresh via GraphQL cache
              logo=""
              userName={
                `${(client?.user?.firstName as string) || ''} ${
                  (client?.user?.lastName as string) || ''
                }`.trim() ||
                (client?.user?.name as string) ||
                ''
              }
              advisorsData={advisorsData || []}
              userAvatar={(client?.user?.avatar as string) || ''}
              userFirstName={(client?.user?.firstName as string) || ''}
              userLastName={(client?.user?.lastName as string) || ''}
              goalEndDate=""
              assigneeOptions={assigneeOptions}
              paramUserId={client?.user?.id || ''}
              initialDescription="" // Empty description - no chat context
            />
          </AppModalBox>
        </Portal>
      )}

      {/* Play Creation Success/Error Modal */}
      {(showPlaySuccessModal || showPlayErrorModal) && createdUserTask && (
        <Portal>
          <AppTaskCreationConfirmation
            showAddTaskSuccessModal={showPlaySuccessModal}
            setShowAddTaskModal={setShowCreatePlayModal}
            setShowAddTaskSuccessModal={setShowPlaySuccessModal}
            setShowAddTaskErrorModal={setShowPlayErrorModal}
            createdUserTask={createdUserTask}
            userId={client?.user?.id || ''}
            userAvatar={(client?.user?.avatar as string) || ''}
            userFirstName={(client?.user?.firstName as string) || ''}
            userLastName={(client?.user?.lastName as string) || ''}
            assigneeOptions={assigneeOptions}
          />
        </Portal>
      )}
    </>
  )
}

export default MessagingInput
